import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  inject,
  signal,
} from '@angular/core';
import {
  SharedContact,
  SharedRecipient,
} from '@compumark/order-management-client';
import { cloneDeep } from 'lodash';

import { SharingService } from '../../services/sharing.service';
import { SelectedRecipient } from '../order-sharing-modal/order-sharing-modal.component';

interface MemberNode {
  name?: string;
  rawName?: string;
  city?: string;
  selected?: boolean;
  indeterminate?: boolean;
  expanded?: boolean;
  memberId?: number;
  contacts?: ContactNode[];
}

interface ContactNode {
  name?: string;
  selected?: boolean;
  contactId?: number;
}

@Component({
  selector: 'app-member-recipients',
  templateUrl: './member-recipients.component.html',
  styleUrls: ['./member-recipients.component.scss'],
  standalone: false,
})
export class MemberRecipientsComponent implements OnInit, OnChanges {
  private sharingService = inject(SharingService);
  protected dataSource = signal<MemberNode[]>([]);

  @Input() public selectedRecipients!: SelectedRecipient[];
  @Output() public selectionChange: EventEmitter<SelectedRecipient[]> =
    new EventEmitter<SelectedRecipient[]>();

  private recipients!: SelectedRecipient[];
  private originalDataSource!: MemberNode[];
  private selectedMap!: Map<string, boolean>;

  ngOnInit(): void {
    this.sharingService.getRecipients().subscribe((recipients) => {
      this.recipients = recipients;
      this.initSelectedMapFromInput();
      this.buildDataSource();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.selectedRecipients && !changes.selectedRecipients.firstChange) {
      this.initSelectedMapFromInput();
      this.updateSelectionOfDataSource();
    }
  }

  private initSelectedMapFromInput(): void {
    this.selectedMap = new Map<string, boolean>();
    this.selectedRecipients?.forEach((sr) => {
      this.selectedMap.set(
        this.selectedMapKey(sr.memberId, sr.contactId),
        true,
      );
    });
  }

  private selectedMapKey(memberId?: number, contactId?: number): string {
    return `${memberId}:${contactId}`;
  }

  private buildDataSource(): void {
    this.dataSource.set([]);
    const nodes: MemberNode[] = [];
    // Sort recipients by memberName
    const sortedRecipients = [...(this.recipients ?? [])].sort((a, b) =>
      (a.memberName || '').localeCompare(b.memberName || ''),
    );
    sortedRecipients.forEach((recipient: SharedRecipient) => {
      // Sort contacts by contactName
      const sortedContacts = (recipient.contacts ?? [])
        .slice()
        .sort((a, b) =>
          (a.contactName || '').localeCompare(b.contactName || ''),
        );
      const memberNode: MemberNode = {
        name: recipient.memberName + ' (' + recipient.memberCity + ')',
        rawName: recipient.memberName,
        city: recipient.memberCity,
        memberId: recipient.memberId,
        contacts: sortedContacts.map(
          (contact: SharedContact): ContactNode => ({
            name: contact.contactName,
            contactId: contact.contactId,
          }),
        ),
      };
      nodes.push(memberNode);
    });
    this.dataSource.set(nodes);
    this.updateSelectionOfDataSource();
    this.originalDataSource = cloneDeep(this.dataSource());
  }

  toggleExpand(node: MemberNode): void {
    node.expanded = !node.expanded;
  }

  toggleMember(member: MemberNode, checked: boolean): void {
    member.contacts?.forEach((contact: ContactNode) => {
      contact.selected = checked;
      this.selectedMap.set(
        this.selectedMapKey(member.memberId, contact.contactId),
        checked,
      );
    });
    this.updateMemberSelection(member);
    this.emitSelectedRecipients();
  }

  toggleContact(
    member: MemberNode,
    contact: ContactNode,
    checked: boolean,
  ): void {
    contact.selected = checked;
    this.selectedMap.set(
      this.selectedMapKey(member.memberId, contact.contactId),
      checked,
    );
    this.updateMemberSelection(member);
    this.emitSelectedRecipients();
  }

  emitSelectedRecipients(): void {
    const selectedNodes: SelectedRecipient[] = [];
    this.originalDataSource.forEach((memberNode: MemberNode) => {
      memberNode.contacts?.forEach((contactNode: ContactNode) => {
        if (
          this.selectedMap.get(
            this.selectedMapKey(memberNode.memberId, contactNode.contactId),
          )
        ) {
          selectedNodes.push({
            memberName: memberNode.rawName,
            memberCity: memberNode.city,
            memberId: memberNode.memberId,
            contactName: contactNode.name,
            contactId: contactNode.contactId,
          });
        }
      });
    });
    this.selectionChange.emit(selectedNodes);
  }

  private updateSelectionOfDataSource(): void {
    this.dataSource().forEach((member) => {
      member.contacts?.forEach((contact) => {
        contact.selected = this.selectedMap.get(
          this.selectedMapKey(member.memberId, contact.contactId),
        );
      });
      this.updateMemberSelection(member);
    });
  }

  private updateMemberSelection(member: MemberNode): void {
    const total = member.contacts?.length || 0;
    const selectedCount =
      member.contacts?.filter((c) => c.selected).length || 0;

    if (selectedCount === 0) {
      member.selected = false;
      member.indeterminate = false;
    } else if (selectedCount === total) {
      member.selected = true;
      member.indeterminate = false;
    } else {
      member.selected = false;
      member.indeterminate = true;
    }
  }

  onFilterChanged($event: Event): void {
    const filterValue = (($event.target as HTMLInputElement).value || '')
      .trim()
      .toLowerCase();
    if (!filterValue) {
      this.dataSource.set(cloneDeep(this.originalDataSource));
    } else {
      const filtered: MemberNode[] = [];
      this.originalDataSource.forEach((member) => {
        const memberMatches = member.name?.toLowerCase()?.includes(filterValue);
        const anyContactMatches = member.contacts?.some(
          (contact: ContactNode) =>
            contact.name?.toLowerCase().includes(filterValue),
        );
        if (memberMatches || anyContactMatches) {
          // Sort contacts by contactName
          const sortedContacts = (member.contacts ?? [])
            .slice()
            .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
          filtered.push({ ...cloneDeep(member), contacts: sortedContacts });
        }
      });
      // Sort filtered members by memberName
      this.dataSource.set(
        filtered.sort((a, b) =>
          (a.rawName || '').localeCompare(b.rawName || ''),
        ),
      );
    }
    this.updateSelectionOfDataSource();
  }
}
